import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  DeliveryOptionDescription,
  DeliveryOptionPriceBadge,
  getShippingAddressesFromLineItem,
  ParsedShippingAddressPerLineItem,
} from '@printdeal/checkout-frontend';
import { Alert, DescriptionList } from '@printdeal/ui-library';
import { getCustomLineItemsDeliveryService, CustomLineItem } from '@printdeal/business-logic';
import { StoreToggle } from '@printdeal/feature-management/react';
import { SanitizedLineItem } from '../../../../../types/order/Order';
import ShippingAddress from './ShippingAddress';
import OrderDetailContext from '../../contexts/OrderDetailContext';
import DeliveriesLink from './DeliveriesLink';

interface LineItemShippingProps {
  lineItem: SanitizedLineItem;
}

interface AnyObjectProps {
  [key: string]: any;
}

interface DeliveryOptionsProps {
  [key: string]: any;
}

const getRecsKey = (str: string) => str.split(':')[1].split('-')[0];

interface CarrierOptionsProps {
  postnl: string,
  dhl: string,
}

const carrierOptions: CarrierOptionsProps = {
  postnl: 'PostNL',
  dhl: 'DHL',
};

const LineItemShipping = ({ lineItem }: LineItemShippingProps) => {
  const { order } = useContext(OrderDetailContext);
  const [pendingChange, setPendingChange] = useState<boolean>(false);
  const shippingAddresses = getShippingAddressesFromLineItem(lineItem) || [] as Array<ParsedShippingAddressPerLineItem>;

  const {
    custom: { fields: { mcpAddressChangeStatus, qpShippingCarrierKey, timedDelivery } },
    sane: { unknownDeliveries, postalCodeDeliveries },
  } = lineItem;

  const deliveryServiceCustomLineItems = getCustomLineItemsDeliveryService(order?.customLineItems as CustomLineItem[]);

  const getDeliveryService = () => (
    deliveryServiceCustomLineItems.find((customLineItem) => (
      customLineItem.custom?.fields?.lineItemReference === lineItem.id))
  );

  const carrierKey = qpShippingCarrierKey?.length > 0 ? getRecsKey(qpShippingCarrierKey[0]) : null;

  const [lineItemDeliveryService] = useState(getDeliveryService());
  const getDeliveryServicePrice = () => (
    lineItemDeliveryService?.totalPrice?.centAmount ? lineItemDeliveryService.totalPrice.centAmount / 100 : null
  );
  const deliveryOption: DeliveryOptionsProps | undefined = {};
  if (carrierKey) {
    deliveryOption.carrier = carrierKey;
    deliveryOption.recsKey = carrierOptions[carrierKey as keyof CarrierOptionsProps] || 'all';
    deliveryOption.before = timedDelivery ? (JSON.parse(timedDelivery) as AnyObjectProps)?.before : null;
    deliveryOption.price = {
      price: getDeliveryServicePrice(),
    };
  }

  return (
    <>
      {(mcpAddressChangeStatus === 'initial' || pendingChange)
        && (
          <Alert variant="info" className="pd-my-3">
            <FormattedMessage
              id="account.order.shippingAddress.changeRequest.pending"
              defaultMessage="A change request is pending for one or more line items in this address."
            />
          </Alert>
        )}
      {mcpAddressChangeStatus === 'failed'
        && (
          <Alert variant="info" className="pd-my-3">
            <FormattedMessage
              id="account.order.shippingAddress.changeRequest.failed"
              defaultMessage="The change request for one or more line items has failed."
            />
          </Alert>
        )}
      <ShippingAddress
        lineItem={lineItem}
        address={shippingAddresses[0]}
        setPendingChange={setPendingChange}
        isMainAddress
      />
      {
        shippingAddresses.splice(1).map((shippingAddress) => (
          <ShippingAddress
            key={shippingAddress.id}
            lineItem={lineItem}
            address={shippingAddress}
            setPendingChange={setPendingChange}
          />
        ))
      }
      {shippingAddresses.length > 1 && unknownDeliveries.length > 0 && (
        <DescriptionList className="pd-mt-3 md:pd-grid-cols-3">
          <DescriptionList.Title>
            {Object.keys(postalCodeDeliveries).length > 0 ? (
              <FormattedMessage
                id="account.order.orderline.trackAndTrace.links.other"
                defaultMessage="Other tracking links"
              />
            ) : (
              <FormattedMessage
                id="account.order.orderline.trackAndTrace.links"
                defaultMessage="Tracking links"
              />
            )}
          </DescriptionList.Title>
          <DescriptionList.Content className="md:pd-col-span-2">
            {
              unknownDeliveries.length > 0 && (
                <ul className="pd-mb-3">
                  {unknownDeliveries.map((link: string) => (
                    <li key={link}>
                      <DeliveriesLink trackingLink={link} />
                    </li>
                  ))}
                </ul>
              )
            }
          </DescriptionList.Content>
        </DescriptionList>
      )}
      <StoreToggle id="drukwerkdeal-nl_printdeal-be">
        <StoreToggle.Enabled>
          {carrierKey && (
            <DescriptionList className="pd-mt-3 md:pd-grid-cols-3">
              <DescriptionList.Title>
                <FormattedMessage
                  id="account.order.orderline.shipping.service.provider"
                  defaultMessage="Delivery person and time"
                />
              </DescriptionList.Title>
              <DescriptionList.Content className="pd-flex md:pd-col-span-2">
                <span className="pd-mr-2">{deliveryOption.recsKey}</span>
                <span className="pd-mr-2 pd-text-dim-500"><DeliveryOptionDescription deliveryOption={deliveryOption} /></span>
                <DeliveryOptionPriceBadge deliveryOption={deliveryOption} />
              </DescriptionList.Content>
            </DescriptionList>
          )}
        </StoreToggle.Enabled>
      </StoreToggle>
    </>
  );
};

export default LineItemShipping;
